import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { isApiError } from '@/lib/api/errors';
import { useErrorStore } from '@/store/useErrorStore';

/**
 * The global error banner is the DEFAULT for every failed query and mutation
 * (house convention). A screen that shows its error inline opts out with
 * `meta: { silent: true }`. Never shown for 401 — the client already ran
 * refresh → sign-out.
 */
declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: { silent?: boolean };
    mutationMeta: { silent?: boolean };
  }
}

export function reportApiError(error: unknown, silent?: boolean): void {
  if (silent || !isApiError(error) || error.status === 401) return;
  useErrorStore.getState().show({ message: error.userMessage, correlationId: error.correlationId });
}

/** 4xx are answers, not blips — don't retry them. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (isApiError(error) && error.status >= 400 && error.status < 500) return false;
  return failureCount < 3;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({ onError: (e, q) => reportApiError(e, q.meta?.silent) }),
    mutationCache: new MutationCache({ onError: (e, _v, _c, m) => reportApiError(e, m.meta?.silent) }),
    defaultOptions: {
      queries: { retry: shouldRetry, staleTime: 30_000 },
      mutations: { retry: false },
    },
  });
}

export const queryClient = createQueryClient();
